import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { carVariantAPI, BASE_URL } from '../services/api';
import './Brands.css';
import Icon from '../components/Icon';

const CarVariantDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [variant, setVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedColorIdx, setSelectedColorIdx] = useState(0);
  const [activeSpecTab, setActiveSpecTab] = useState(null);
  const [activeFeatureTab, setActiveFeatureTab] = useState(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    fetchVariantDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchVariantDetails = async () => {
    try {
      setLoading(true);
      const response = await carVariantAPI.getById(id);
      setVariant(response.data);

      // Set default tabs
      if (response.data.specs && response.data.specs.length > 0) {
        const categories = [...new Set(response.data.specs.map(s => s.category))];
        setActiveSpecTab(categories[0]);
      }
      if (response.data.features && response.data.features.length > 0) {
        const categories = [...new Set(response.data.features.map(f => f.category))];
        setActiveFeatureTab(categories[0]);
      }

      setError('');
    } catch (error) {
      console.error('Error fetching variant details:', error);
      setError('Failed to load variant details');
    } finally {
      setLoading(false);
    }
  };

  // Edit handlers
  const handleEditClick = () => {
    setEditFormData({
      variantName: variant.variantName || '',
      price: variant.price || '',
      exShowroomPrice: variant.exShowroomPrice || '',
      onRoadPrice: variant.onRoadPrice || '',
      fuelType: variant.fuelType || '',
      transmissionType: variant.transmissionType || '',
      engine: variant.engine || '',
      power: variant.power || '',
      torque: variant.torque || '',
      mileage: variant.mileage || '',
      seatingCapacity: variant.seatingCapacity || '',
      bootSpace: variant.bootSpace || '',
      fuelTankCapacity: variant.fuelTankCapacity || '',
      groundClearance: variant.groundClearance || '',
      topSpeed: variant.topSpeed || '',
      driveType: variant.driveType || '',
      ncapRating: variant.ncapRating || '',
      length: variant.length || '',
      width: variant.width || '',
      height: variant.height || '',
      wheelBase: variant.wheelBase || '',
      kerbWeight: variant.kerbWeight || '',
      acceleration: variant.acceleration || ''
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await carVariantAPI.update(id, editFormData);
      setShowEditModal(false);
      fetchVariantDetails();
    } catch (err) {
      console.error('Error updating variant:', err);
      setError(err.response?.data?.error || 'Failed to update variant');
    } finally {
      setEditLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    if (typeof price === 'string') {
      // Check if already formatted with currency indicators (including abbreviations)
      if (price.includes('Lakh') || price.includes('Crore') || price.includes('Rs') ||
          price.includes('Cr') || price.includes('L') || price.includes('₹')) {
        return price;
      }
      const numPrice = parseFloat(price.replace(/[^0-9.]/g, ''));
      if (!isNaN(numPrice) && numPrice > 0) {
        if (numPrice >= 10000000) {
          return `Rs. ${(numPrice / 10000000).toFixed(2)} Crore`;
        } else if (numPrice >= 100000) {
          return `Rs. ${(numPrice / 100000).toFixed(2)} Lakh`;
        }
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(numPrice);
      }
      return price;
    }
    if (typeof price === 'number') {
      if (price >= 10000000) {
        return `Rs. ${(price / 10000000).toFixed(2)} Crore`;
      } else if (price >= 100000) {
        return `Rs. ${(price / 100000).toFixed(2)} Lakh`;
      }
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);
    }
    return '-';
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${BASE_URL}${imagePath}`;
  };

  const getVariantColors = () => {
    if (variant?.colors && Array.isArray(variant.colors) && variant.colors.length > 0) {
      return variant.colors;
    }
    return [];
  };

  // Group specs by category
  const getSpecsByCategory = () => {
    if (!variant?.specs || variant.specs.length === 0) return {};
    return variant.specs.reduce((acc, spec) => {
      if (!acc[spec.category]) acc[spec.category] = [];
      acc[spec.category].push(spec);
      return acc;
    }, {});
  };

  // Group features by category
  const getFeaturesByCategory = () => {
    if (!variant?.features || variant.features.length === 0) return {};
    return variant.features.reduce((acc, feature) => {
      if (!acc[feature.category]) acc[feature.category] = [];
      acc[feature.category].push(feature);
      return acc;
    }, {});
  };

  if (loading) {
    return (
      <div className="brands-page">
        <div className="loading">Loading variant details...</div>
      </div>
    );
  }

  if (error || !variant) {
    return (
      <div className="brands-page">
        <div className="alert alert-error">{error || 'Variant not found'}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/car-variants')}>
          Back to Variants
        </button>
      </div>
    );
  }

  const colors = getVariantColors();
  const selectedColor = colors[selectedColorIdx] || null;
  const displayImage = selectedColor?.colorImage || variant.image;
  const specsByCategory = getSpecsByCategory();
  const featuresByCategory = getFeaturesByCategory();
  const specCategories = Object.keys(specsByCategory);
  const featureCategories = Object.keys(featuresByCategory);

  return (
    <div className="brands-page">
      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Car Variant</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                {/* Basic Info */}
                <div className="form-group">
                  <label>Variant Name *</label>
                  <input
                    type="text"
                    name="variantName"
                    value={editFormData.variantName}
                    onChange={handleEditFormChange}
                    required
                  />
                </div>

                {/* Pricing Section */}
                <div className="form-section-title">Pricing</div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Price</label>
                    <input
                      type="text"
                      name="price"
                      value={editFormData.price}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 10.5 Lakh"
                    />
                  </div>
                  <div className="form-group">
                    <label>Ex-Showroom Price</label>
                    <input
                      type="text"
                      name="exShowroomPrice"
                      value={editFormData.exShowroomPrice}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 10.5 Lakh"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>On-Road Price</label>
                    <input
                      type="text"
                      name="onRoadPrice"
                      value={editFormData.onRoadPrice}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 12.5 Lakh"
                    />
                  </div>
                  <div className="form-group"></div>
                </div>

                {/* Engine & Performance Section */}
                <div className="form-section-title">Engine & Performance</div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Fuel Type</label>
                    <select name="fuelType" value={editFormData.fuelType} onChange={handleEditFormChange}>
                      <option value="">Select Fuel Type</option>
                      <option value="Petrol">Petrol</option>
                      <option value="Diesel">Diesel</option>
                      <option value="Electric">Electric</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="CNG">CNG</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Transmission</label>
                    <select name="transmissionType" value={editFormData.transmissionType} onChange={handleEditFormChange}>
                      <option value="">Select Transmission</option>
                      <option value="Manual">Manual</option>
                      <option value="Automatic">Automatic</option>
                      <option value="AMT">AMT</option>
                      <option value="CVT">CVT</option>
                      <option value="DCT">DCT</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Engine</label>
                    <input
                      type="text"
                      name="engine"
                      value={editFormData.engine}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 1197 cc"
                    />
                  </div>
                  <div className="form-group">
                    <label>Drive Type</label>
                    <select name="driveType" value={editFormData.driveType} onChange={handleEditFormChange}>
                      <option value="">Select Drive Type</option>
                      <option value="FWD">FWD</option>
                      <option value="RWD">RWD</option>
                      <option value="AWD">AWD</option>
                      <option value="4WD">4WD</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Power</label>
                    <input
                      type="text"
                      name="power"
                      value={editFormData.power}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 88 bhp"
                    />
                  </div>
                  <div className="form-group">
                    <label>Torque</label>
                    <input
                      type="text"
                      name="torque"
                      value={editFormData.torque}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 113 Nm"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Mileage</label>
                    <input
                      type="text"
                      name="mileage"
                      value={editFormData.mileage}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 20.4 kmpl"
                    />
                  </div>
                  <div className="form-group">
                    <label>Top Speed</label>
                    <input
                      type="text"
                      name="topSpeed"
                      value={editFormData.topSpeed}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 180 kmph"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Acceleration (0-100 kmph)</label>
                    <input
                      type="text"
                      name="acceleration"
                      value={editFormData.acceleration}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 12.5 sec"
                    />
                  </div>
                  <div className="form-group"></div>
                </div>

                {/* Dimensions Section */}
                <div className="form-section-title">Dimensions</div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Length</label>
                    <input
                      type="text"
                      name="length"
                      value={editFormData.length}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 3995 mm"
                    />
                  </div>
                  <div className="form-group">
                    <label>Width</label>
                    <input
                      type="text"
                      name="width"
                      value={editFormData.width}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 1735 mm"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Height</label>
                    <input
                      type="text"
                      name="height"
                      value={editFormData.height}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 1640 mm"
                    />
                  </div>
                  <div className="form-group">
                    <label>Wheel Base</label>
                    <input
                      type="text"
                      name="wheelBase"
                      value={editFormData.wheelBase}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 2500 mm"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Ground Clearance</label>
                    <input
                      type="text"
                      name="groundClearance"
                      value={editFormData.groundClearance}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 165 mm"
                    />
                  </div>
                  <div className="form-group">
                    <label>Kerb Weight</label>
                    <input
                      type="text"
                      name="kerbWeight"
                      value={editFormData.kerbWeight}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 1150 kg"
                    />
                  </div>
                </div>

                {/* Capacity & Safety Section */}
                <div className="form-section-title">Capacity & Safety</div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Seating Capacity</label>
                    <input
                      type="text"
                      name="seatingCapacity"
                      value={editFormData.seatingCapacity}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 5"
                    />
                  </div>
                  <div className="form-group">
                    <label>Boot Space</label>
                    <input
                      type="text"
                      name="bootSpace"
                      value={editFormData.bootSpace}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 378 L"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Fuel Tank Capacity</label>
                    <input
                      type="text"
                      name="fuelTankCapacity"
                      value={editFormData.fuelTankCapacity}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 37 L"
                    />
                  </div>
                  <div className="form-group">
                    <label>NCAP Rating</label>
                    <input
                      type="text"
                      name="ncapRating"
                      value={editFormData.ncapRating}
                      onChange={handleEditFormChange}
                      placeholder="e.g., 5 Star"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={editLoading}>
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="page-header">
        <div className="breadcrumb">
          <button className="btn btn-link" onClick={() => navigate('/car-variants')}>
            Car Variants
          </button>
          <span className="breadcrumb-separator">/</span>
          <span>{variant.variantName}</span>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleEditClick}>
            Edit Variant
          </button>
        </div>
      </div>

      <div className="details-container">
        {/* Main Info Card */}
        <div className="details-card details-main">
          <div className="details-image-section">
            <div className="details-image">
              {displayImage ? (
                <img
                  src={getImageUrl(displayImage)}
                  alt={`${variant.variantName}${selectedColor ? ` - ${selectedColor.colorName}` : ''}`}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="no-image-placeholder large">No Image Available</div>
              )}
            </div>

            {/* Color Swatches */}
            {colors.length > 0 && (
              <div className="details-colors">
                <h4>Available Colors ({colors.length})</h4>
                <div className="color-swatches-large">
                  {colors.map((color, idx) => {
                    const isSelected = selectedColorIdx === idx;
                    const colorHex = color.colorCode ? `#${color.colorCode.replace('#', '')}` : '#888';
                    const dualColors = color.dualColorCode ? color.dualColorCode.split(',') : null;

                    return (
                      <div
                        key={idx}
                        className={`color-swatch-large ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedColorIdx(idx)}
                      >
                        <div
                          className="color-circle"
                          style={{
                            backgroundColor: colorHex,
                            backgroundImage: dualColors
                              ? `linear-gradient(135deg, ${dualColors[0]} 50%, ${dualColors[1] || dualColors[0]} 50%)`
                              : 'none'
                          }}
                        />
                        <span className="color-name">{color.colorName}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="details-info-section">
            <div className="details-header">
              <h1>{variant.variantName}</h1>
              <p className="details-subtitle">
                {variant.model?.brand?.brandName} {variant.model?.name}
              </p>
            </div>

            <div className="details-price-section">
              <div className="price-box">
                <span className="price-label">Ex-Showroom Price</span>
                <span className="price-value primary">{formatPrice(variant.exShowroomPrice || variant.price)}</span>
              </div>
              {variant.onRoadPrice && (
                <div className="price-box">
                  <span className="price-label">On-Road Price</span>
                  <span className="price-value">{formatPrice(variant.onRoadPrice)}</span>
                </div>
              )}
            </div>

            <div className="details-quick-specs">
              {variant.fuelType && (
                <div className="quick-spec">
                  <span className="spec-icon">&#9981;</span>
                  <span>{variant.fuelType}</span>
                </div>
              )}
              {variant.transmissionType && (
                <div className="quick-spec">
                  <span className="spec-icon">&#9881;</span>
                  <span>{variant.transmissionType}</span>
                </div>
              )}
              {variant.engine && (
                <div className="quick-spec">
                  <span className="spec-icon">&#9889;</span>
                  <span>{variant.engine}</span>
                </div>
              )}
              {variant.mileage && (
                <div className="quick-spec">
                  <span className="spec-icon">&#128663;</span>
                  <span>{variant.mileage}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Specifications Card - from API */}
        {specCategories.length > 0 && (
          <div className="details-card">
            <h3>Specifications</h3>
            <div className="category-tabs">
              {specCategories.map(category => (
                <button
                  key={category}
                  className={`category-tab ${activeSpecTab === category ? 'active' : ''}`}
                  onClick={() => setActiveSpecTab(category)}
                >
                  {category}
                </button>
              ))}
            </div>
            <div className="specs-grid">
              {activeSpecTab && specsByCategory[activeSpecTab]?.map((spec, idx) => (
                <div key={idx} className="spec-row">
                  <span className="spec-label">{spec.specName}</span>
                  <span className="spec-value">{spec.specValue || '-'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Features Card - from API */}
        {featureCategories.length > 0 && (
          <div className="details-card">
            <h3>Features</h3>
            <div className="category-tabs">
              {featureCategories.map(category => (
                <button
                  key={category}
                  className={`category-tab ${activeFeatureTab === category ? 'active' : ''}`}
                  onClick={() => setActiveFeatureTab(category)}
                >
                  {category}
                </button>
              ))}
            </div>
            <div className="features-grid">
              {activeFeatureTab && featuresByCategory[activeFeatureTab]?.map((feature, idx) => (
                <div key={idx} className={`feature-item ${feature.available ? 'available' : 'unavailable'}`}>
                  <span className="feature-icon"><Icon name={feature.available ? 'check' : 'close'} size={14} /></span>
                  <span className="feature-name">{feature.featureName}</span>
                  {feature.featureValue && feature.featureValue !== 'Yes' && feature.featureValue !== 'No' && (
                    <span className="feature-value">{feature.featureValue}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fallback Specifications if no API specs */}
        {specCategories.length === 0 && (
          <div className="details-card">
            <h3>Specifications</h3>
            <div className="specs-grid">
              <div className="spec-row">
                <span className="spec-label">Engine</span>
                <span className="spec-value">{variant.engine || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Power</span>
                <span className="spec-value">{variant.power || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Torque</span>
                <span className="spec-value">{variant.torque || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Mileage</span>
                <span className="spec-value">{variant.mileage || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Fuel Type</span>
                <span className="spec-value">{variant.fuelType || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Transmission</span>
                <span className="spec-value">{variant.transmissionType || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Seating Capacity</span>
                <span className="spec-value">{variant.seatingCapacity || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Boot Space</span>
                <span className="spec-value">{variant.bootSpace || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Fuel Tank Capacity</span>
                <span className="spec-value">{variant.fuelTankCapacity || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Ground Clearance</span>
                <span className="spec-value">{variant.groundClearance || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Top Speed</span>
                <span className="spec-value">{variant.topSpeed || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Drive Type</span>
                <span className="spec-value">{variant.driveType || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">NCAP Rating</span>
                <span className="spec-value">{variant.ncapRating || '-'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Additional Info Card */}
        <div className="details-card">
          <h3>Additional Information</h3>
          <div className="specs-grid">
            <div className="spec-row">
              <span className="spec-label">Variant ID</span>
              <span className="spec-value">{variant.variantId || variant.id}</span>
            </div>
            <div className="spec-row">
              <span className="spec-label">Slug</span>
              <span className="spec-value">{variant.slug}</span>
            </div>
            {variant.variantUrl && (
              <div className="spec-row">
                <span className="spec-label">Source URL</span>
                <span className="spec-value">
                  <a href={`https://www.cardekho.com${variant.variantUrl}`} target="_blank" rel="noopener noreferrer">
                    View on CardDekho
                  </a>
                </span>
              </div>
            )}
            <div className="spec-row">
              <span className="spec-label">Created At</span>
              <span className="spec-value">{new Date(variant.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="spec-row">
              <span className="spec-label">Updated At</span>
              <span className="spec-value">{new Date(variant.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarVariantDetails;
